import React from "react";
import {
  toDateKey,
  minutesToTimeStr,
  excursionToMinutes,
  roundToHalfHour,
  HALF_HOUR,
  getClosestStartTimeForDay,
  addOneHour,
  planningTooltipDragWantsCopy,
  setPlanningTooltipPersonChipDropEffect,
  mergeExcursionsAfterPut,
} from "../../shared/utils/planningUtils";
import { useExcursion } from "contexts/api";
import type {
  Excursion,
  ExcursionChipDropPending,
  ExcursionGroupRequest,
  ExcursionRequest,
  ExcursionBooking,
  BookingStatus,
  ExcursionCustomer,
  ExcursionUser,
} from "api/excursions/Excursion.typed";
import { useAlert } from "contexts/app/notificationToast";
import { useExcursionTemplates } from "contexts/app/excursionsData";
import {
  toGroupPayload,
  baseExcPayload,
  groupColorOrFallback,
} from "../../shared/utils/excursionPayloadUtils";
import { filterTemplateToRecord } from "../../shared/utils/excursionUtils";
import { useExcursionCalendar } from "./useExcursionCalendar";
import { useExcursionData } from "./useExcursionData";
import { useExcursionLabels } from "./useExcursionLabels";
import { useExcursionPlanningPermissions } from "./useExcursionPlanningPermissions";
import { useExcursionSSE } from "./useExcursionSSE";

export type ExcursionsPlanningStateProps = {
  showTimeSlots?: boolean;
  hoursStart?: number;
  hoursEnd?: number;
};

function collectUserIdsFromGroups(groups: ExcursionGroupRequest[]): number[] {
  const ids = new Set<number>();
  for (const g of groups) {
    for (const u of g.excursion_users ?? []) {
      ids.add(u.id);
    }
  }
  return Array.from(ids);
}

/** Debounced dragleave clears when moving through the gap between anchor card and portaled tooltip. */
const CHIP_DRAG_POPOVER_LEAVE_CLEAR_MS = 140;

export function useExcursionsPlanningState({
  showTimeSlots = true,
  hoursStart = 0,
  hoursEnd = 24,
}: ExcursionsPlanningStateProps = {}) {
  const {
    getExcursion,
    createExcursion,
    updateExcursion,
    deleteExcursion,
    listExcursionBookings,
    createExcursionBooking,
  } = useExcursion();
  const { showAlert } = useAlert();
  const {
    canEdit,
    canSendSMS,
    canOpenExcursionDrawer,
    canListBookingsForPlanning,
    canSyncBookingsOnSave,
  } = useExcursionPlanningPermissions();

  const calendar = useExcursionCalendar();
  const { viewMode, currentDate } = calendar;
  const data = useExcursionData(viewMode, currentDate);
  const {
    excursions,
    setExcursions,
    loadExcursions,
    silentReload,
    getExcursionsForDay,
    getExcursionsForSlot,
    periodBounds,
  } = data;

  const [modalDay, setModalDay] = React.useState<Date | null>(null);
  const [editingExcursion, setEditingExcursion] = React.useState<Excursion | null>(null);
  const [modalInitialStart, setModalInitialStart] = React.useState<string | null>(null);
  const [modalInitialEnd, setModalInitialEnd] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!editingExcursion) return;
    const fresh = excursions.find((exc) => exc.id === editingExcursion.id);
    if (fresh && fresh !== editingExcursion) {
      setEditingExcursion(fresh);
    }
  }, [excursions, editingExcursion]);
  const [groupsDrawerExcursionId, setGroupsDrawerExcursionId] = React.useState<number | null>(null);
  /** Popper `forceOpen` excursion id while dragging a client/guide chip over the grid. */
  const [planningChipDragTooltipTargetId, setPlanningChipDragTooltipTargetId] = React.useState<
    number | null
  >(null);
  const groupsDrawerPostDropTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [draggingExcursion, setDraggingExcursion] = React.useState<{
    excursionId: number;
    durationMinutes: number;
    dragOffsetY: number;
  } | null>(null);
  const [dragProjection, setDragProjection] = React.useState<{
    day: Date;
    startMinutes: number;
  } | null>(null);
  const [clientChipDragInProgress, setClientChipDragInProgress] = React.useState(false);
  const [clientChipDragSourceExcursionId, setClientChipDragSourceExcursionId] = React.useState<
    number | null
  >(null);
  /** True while dragging a PersonChip JSON payload; synced for handleDragEnterCard (avoids stale closure). */
  const clientChipDragInProgressRef = React.useRef(false);
  const chipDragFinalizeSessionRef = React.useRef(0);
  const lastAppliedChipDragFinalizeSessionRef = React.useRef<number | null>(null);
  /** Last pointer during client-chip drag (dragend coords can be unreliable). */
  const lastPointerDuringClientChipDragRef = React.useRef({ x: 0, y: 0 });
  const chipDragPopoverLeaveClearRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [chipDropPending, setChipDropPending] = React.useState<ExcursionChipDropPending | null>(
    null,
  );
  const chipDropEpochRef = React.useRef(0);

  const withChipDropPending = React.useCallback(
    async (pending: ExcursionChipDropPending, work: () => Promise<void>) => {
      const epoch = ++chipDropEpochRef.current;
      setChipDropPending(pending);
      try {
        await work();
      } finally {
        if (chipDropEpochRef.current === epoch) {
          setChipDropPending(null);
        }
      }
    },
    [],
  );

  React.useEffect(() => {
    if (!clientChipDragInProgress) return undefined;
    const onMove = (ev: PointerEvent) => {
      lastPointerDuringClientChipDragRef.current = { x: ev.clientX, y: ev.clientY };
    };
    window.addEventListener("pointermove", onMove, true);
    window.addEventListener("pointerdown", onMove, true);
    return () => {
      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("pointerdown", onMove, true);
    };
  }, [clientChipDragInProgress]);

  React.useEffect(() => {
    if (!clientChipDragInProgress) return undefined;
    const onDragEv = (ev: DragEvent) => {
      if (Number.isFinite(ev.clientX) && Number.isFinite(ev.clientY)) {
        lastPointerDuringClientChipDragRef.current = { x: ev.clientX, y: ev.clientY };
      }
    };
    window.addEventListener("drag", onDragEv, true);

    return () => {
      window.removeEventListener("drag", onDragEv, true);
    };
  }, [clientChipDragInProgress]);

  const cancelChipDragPopoverLeaveClear = React.useCallback(() => {
    if (chipDragPopoverLeaveClearRef.current) {
      clearTimeout(chipDragPopoverLeaveClearRef.current);
      chipDragPopoverLeaveClearRef.current = null;
    }
  }, []);

  const { groupTemplates, excursionTemplates } = useExcursionTemplates();

  const defaultGroupTemplateId = React.useMemo(() => {
    const arr = Array.isArray(groupTemplates) ? groupTemplates : [];
    const t = arr.find((x) => x.should_be_used_by_default) ?? arr[0];
    return t?.id ?? null;
  }, [groupTemplates]);

  const [bookings, setBookings] = React.useState<ExcursionBooking[]>([]);

  React.useEffect(() => {
    if (!canListBookingsForPlanning || excursions.length === 0) {
      setBookings([]);
      return;
    }
    listExcursionBookings({ from: periodBounds.from, to: periodBounds.to }).then((list) =>
      setBookings(list ?? []),
    );
  }, [canListBookingsForPlanning, excursions, listExcursionBookings, periodBounds]);

  const silentReloadBookings = React.useCallback(async () => {
    if (!canListBookingsForPlanning) return;
    const list = await listExcursionBookings({ from: periodBounds.from, to: periodBounds.to });
    setBookings(list ?? []);
  }, [canListBookingsForPlanning, listExcursionBookings, periodBounds]);

  useExcursionSSE(silentReload, silentReloadBookings);

  const bookingStatusMap = React.useMemo(() => {
    const map: Record<number, Record<number, BookingStatus>> = {};
    for (const b of bookings) {
      if (b.planning_anonymized) continue;
      if (b.status == null) continue;
      if (b.excursion_id == null || b.user_id == null) continue;
      if (!map[b.excursion_id]) map[b.excursion_id] = {};
      map[b.excursion_id][b.user_id] = b.status;
    }
    return map;
  }, [bookings]);

  const bookingUserNameMap = React.useMemo(() => {
    const map: Record<number, string> = {};
    for (const b of bookings) {
      if (b.planning_anonymized) continue;
      if (b.user_id == null) continue;
      if (b.user_name && b.user_name.trim()) {
        map[b.user_id] = b.user_name.trim();
      }
    }
    return map;
  }, [bookings]);

  const { getGroupLabels, getCustomerInProgressTrainingsTooltip, rawCustomers } =
    useExcursionLabels(excursions, bookingUserNameMap);

  const excursionTemplatesList = React.useMemo(
    () =>
      (Array.isArray(excursionTemplates) ? excursionTemplates : []).map((t) => ({
        id: t.id,
        max_people_per_excursion: t.max_people_per_excursion,
        max_groups_per_excursion: t.max_groups_per_excursion,
        additional_param_fields: filterTemplateToRecord(
          (t.params_template ?? {}) as Record<string, unknown>,
        ),
      })),
    [excursionTemplates],
  );

  const closePlanningGroupsDrawer = React.useCallback(() => {
    if (groupsDrawerPostDropTimerRef.current) {
      clearTimeout(groupsDrawerPostDropTimerRef.current);
      groupsDrawerPostDropTimerRef.current = null;
    }
    setGroupsDrawerExcursionId(null);
  }, []);

  const openPlanningGroupsDrawer = React.useCallback((excursionId: number) => {
    setGroupsDrawerExcursionId(excursionId);
  }, []);

  const finalizePlanningChipDragAfterHtml5 = React.useCallback(
    (_event: DragEvent | null) => {
      const wasClientChipDrag = clientChipDragInProgressRef.current;
      const sessionSnapshot = chipDragFinalizeSessionRef.current;

      if (wasClientChipDrag && lastAppliedChipDragFinalizeSessionRef.current === sessionSnapshot) {
        return;
      }

      cancelChipDragPopoverLeaveClear();

      clientChipDragInProgressRef.current = false;
      setDraggingExcursion(null);
      setDragProjection(null);
      setClientChipDragInProgress(false);
      setClientChipDragSourceExcursionId(null);

      if (!wasClientChipDrag) {
        setPlanningChipDragTooltipTargetId(null);
        lastAppliedChipDragFinalizeSessionRef.current = null;
        return;
      }

      lastAppliedChipDragFinalizeSessionRef.current = sessionSnapshot;
    },
    [cancelChipDragPopoverLeaveClear],
  );

  const dismissPlanningForcedPopoverTarget = React.useCallback(() => {
    setPlanningChipDragTooltipTargetId(null);
  }, []);

  React.useEffect(() => {
    const onDragEndWindow = (evt: DragEvent) => {
      finalizePlanningChipDragAfterHtml5(evt);
    };
    window.addEventListener("dragend", onDragEndWindow, true);
    return () => {
      window.removeEventListener("dragend", onDragEndWindow, true);
      cancelChipDragPopoverLeaveClear();
    };
  }, [finalizePlanningChipDragAfterHtml5, cancelChipDragPopoverLeaveClear]);

  const handleDragEnterCard = React.useCallback(
    (excursionId: number) => {
      if (!clientChipDragInProgressRef.current) return;
      cancelChipDragPopoverLeaveClear();
      setPlanningChipDragTooltipTargetId(excursionId);
    },
    [cancelChipDragPopoverLeaveClear],
  );

  const handleDragLeaveCard = React.useCallback(
    (excursionId: number, e: React.DragEvent<Element>) => {
      const next = e.relatedTarget;
      const cardEl = e.currentTarget;
      if (next instanceof Node && cardEl.contains(next)) return;
      if (next instanceof Element && next.closest("[data-excursion-tooltip]")) return;

      cancelChipDragPopoverLeaveClear();

      const pointerLikelyOverStayOpenZone = (): boolean => {
        const { x, y } = lastPointerDuringClientChipDragRef.current;
        try {
          if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
          const top = document.elementFromPoint(x, y);
          if (top instanceof Element) {
            if (top.closest("[data-excursion-tooltip]")) return true;
            if (cardEl.contains(top)) return true;
          }
        } catch {
          /* ignore */
        }
        return false;
      };

      window.requestAnimationFrame(() => {
        if (!clientChipDragInProgressRef.current) return;
        if (pointerLikelyOverStayOpenZone()) return;

        chipDragPopoverLeaveClearRef.current = setTimeout(() => {
          chipDragPopoverLeaveClearRef.current = null;
          if (!clientChipDragInProgressRef.current) return;
          window.requestAnimationFrame(() => {
            if (!clientChipDragInProgressRef.current) return;
            if (pointerLikelyOverStayOpenZone()) return;
            setPlanningChipDragTooltipTargetId((prev) => (prev === excursionId ? null : prev));
          });
        }, CHIP_DRAG_POPOVER_LEAVE_CLEAR_MS);
      });
    },
    [cancelChipDragPopoverLeaveClear],
  );

  const onClientChipDragStart = React.useCallback(
    (_sourceExcursionId: number) => {
      cancelChipDragPopoverLeaveClear();
      chipDragFinalizeSessionRef.current += 1;
      lastAppliedChipDragFinalizeSessionRef.current = null;
      clientChipDragInProgressRef.current = true;
      setClientChipDragInProgress(true);
      setClientChipDragSourceExcursionId(_sourceExcursionId);
    },
    [cancelChipDragPopoverLeaveClear],
  );
  const onClientChipDragEnd = React.useCallback(() => {}, []);

  const handleDayClick = React.useCallback(
    (day: Date) => {
      if (!canEdit) return;
      closePlanningGroupsDrawer();
      setEditingExcursion(null);
      setModalDay(day);
      const start = getClosestStartTimeForDay(day);
      const end = addOneHour(start);
      setModalInitialStart(start);
      setModalInitialEnd(end);
    },
    [canEdit, closePlanningGroupsDrawer],
  );

  const openModalForSlot = React.useCallback(
    (day: Date, startMinutes: number, _endMinutes?: number) => {
      if (!canEdit) return;
      closePlanningGroupsDrawer();
      setEditingExcursion(null);
      setModalDay(day);
      const start = minutesToTimeStr(roundToHalfHour(startMinutes));
      const end = addOneHour(start);
      setModalInitialStart(start);
      setModalInitialEnd(end);
    },
    [canEdit, closePlanningGroupsDrawer],
  );

  const handleExcursionClick = React.useCallback(
    (e: React.MouseEvent, ex: Excursion) => {
      e.stopPropagation();
      if (!canEdit) return;
      closePlanningGroupsDrawer();
      setModalDay(null);
      setEditingExcursion(ex);
      getExcursion(ex.id).then((full) => {
        if (full) {
          setExcursions((prev) => prev.map((e2) => (e2.id === full.id ? full : e2)));
          setEditingExcursion(full);
        }
      });
    },
    [canEdit, getExcursion, setExcursions, closePlanningGroupsDrawer],
  );

  const handleCloseModal = React.useCallback(() => {
    setModalDay(null);
    setEditingExcursion(null);
    setModalInitialStart(null);
    setModalInitialEnd(null);
    loadExcursions();
  }, [loadExcursions]);

  const syncBookingsForExcursion = React.useCallback(
    async (excursionId: number, payload: ExcursionRequest) => {
      const userIds = collectUserIdsFromGroups(payload.groups ?? []);
      if (canSyncBookingsOnSave && userIds.length > 0 && createExcursionBooking) {
        const existing = await listExcursionBookings({ excursion_id: excursionId });
        const existingUserIds = new Set((existing ?? []).map((b) => b.user_id));
        await Promise.all(
          userIds
            .filter((uid) => !existingUserIds.has(uid))
            .map((userId) =>
              createExcursionBooking({ excursion_id: excursionId, user_id: userId }),
            ),
        );
      }
    },
    [canSyncBookingsOnSave, listExcursionBookings, createExcursionBooking],
  );

  /** Persists the excursion (e.g. status validated after counter modal) and keeps the edit modal open. */
  const persistExcursionAndKeepModalOpen = React.useCallback(
    async (payload: ExcursionRequest) => {
      if (!editingExcursion) return;
      const updated = await updateExcursion(editingExcursion.id, payload);
      if (!updated) return;
      setExcursions((prev) => prev.map((e2) => (e2.id === updated.id ? updated : e2)));
      setEditingExcursion(updated);
      await syncBookingsForExcursion(updated.id, payload);
    },
    [editingExcursion, updateExcursion, setExcursions, syncBookingsForExcursion],
  );

  const handleSave = React.useCallback(
    async (payload: ExcursionRequest) => {
      let excursionId: number;
      if (editingExcursion) {
        await updateExcursion(editingExcursion.id, payload);
        excursionId = editingExcursion.id;
      } else {
        const created = await createExcursion(payload);
        if (!created) return;
        excursionId = created.id;
      }
      await syncBookingsForExcursion(excursionId, payload);
      handleCloseModal();
    },
    [
      editingExcursion,
      updateExcursion,
      createExcursion,
      syncBookingsForExcursion,
      handleCloseModal,
    ],
  );

  const handleDelete = React.useCallback(
    async (id: number) => {
      await deleteExcursion(id);
      handleCloseModal();
    },
    [deleteExcursion, handleCloseModal],
  );

  const modalOpen = modalDay !== null || editingExcursion !== null;

  React.useEffect(() => {
    if (!modalOpen) return;
    setGroupsDrawerExcursionId(null);
  }, [modalOpen]);

  const initialDate =
    modalDay != null
      ? toDateKey(modalDay)
      : editingExcursion != null
        ? editingExcursion.date.slice(0, 10)
        : toDateKey(new Date());
  const initialStartTime = editingExcursion?.start_time ?? modalInitialStart ?? undefined;
  const initialEndTime = editingExcursion?.end_time ?? modalInitialEnd ?? undefined;

  const handleClientDropOnGroup = React.useCallback(
    async (
      sourceExcursionId: number,
      sourceGroupIndex: number,
      targetExcursionId: number,
      targetGroupIndex: number,
      customerId: number,
      customerColor?: string | null,
      copy = false,
    ) => {
      if (!canEdit) return;
      const sourceEx = excursions.find((e) => e.id === sourceExcursionId);
      const targetEx = excursions.find((e) => e.id === targetExcursionId);
      const sourceGroups = sourceEx?.groups ?? [];
      const targetGroups = targetEx?.groups ?? [];
      if (
        !sourceEx ||
        !targetEx ||
        sourceGroupIndex < 0 ||
        sourceGroupIndex >= sourceGroups.length ||
        targetGroupIndex < 0 ||
        targetGroupIndex > targetGroups.length
      )
        return;
      const droppedColor = groupColorOrFallback(customerColor ?? undefined);
      const customerRow = (
        c: NonNullable<Parameters<typeof toGroupPayload>[0]["excursion_customers"]>[number],
        grp: Parameters<typeof toGroupPayload>[0],
      ) => ({
        id: c.id,
        color: groupColorOrFallback(c.color ?? grp.color),
        comments: c.comments,
      });
      const removeCustomer = (
        g: Parameters<typeof toGroupPayload>[0],
        id: number,
      ): ExcursionGroupRequest => ({
        ...toGroupPayload(g),
        excursion_customers: (g.excursion_customers ?? [])
          .filter((c) => c.id !== id)
          .map((c) => customerRow(c, g)),
      });
      const addCustomer = (
        g: Parameters<typeof toGroupPayload>[0],
        id: number,
        color: string,
      ): ExcursionGroupRequest => {
        const tail: ExcursionCustomer = { id, color };
        return {
          ...toGroupPayload(g),
          excursion_customers: [
            ...(g.excursion_customers ?? []).map((c) => customerRow(c, g)),
            tail,
          ],
        };
      };
      const newGroupPayloadWithCustomer = (): ExcursionGroupRequest => {
        const firstCustomer: ExcursionCustomer = { id: customerId, color: droppedColor };
        return {
          excursion_customers: [firstCustomer],
          excursion_users: [],
          ...(defaultGroupTemplateId != null
            ? { excursion_group_param_id: defaultGroupTemplateId }
            : {}),
          color: droppedColor,
        };
      };
      const buildNewTargetGroups = (groups: typeof targetGroups): ExcursionGroupRequest[] =>
        targetGroupIndex === groups.length
          ? [...groups.map(toGroupPayload), newGroupPayloadWithCustomer()]
          : groups.map((g, i) =>
              i === targetGroupIndex ? addCustomer(g, customerId, droppedColor) : toGroupPayload(g),
            );

      const sameGroupMove =
        !copy && sourceExcursionId === targetExcursionId && sourceGroupIndex === targetGroupIndex;
      if (sameGroupMove) {
        return;
      }
      if (targetGroupIndex < targetGroups.length && !sameGroupMove) {
        const tg = targetGroups[targetGroupIndex];
        if ((tg.excursion_customers ?? []).some((c) => c.id === customerId)) {
          showAlert("Ce client est déjà dans ce groupe.", "warning");
          return;
        }
      }

      const results: Excursion[] = [];

      if (copy) {
        if (sourceExcursionId === targetExcursionId) {
          const mergedGroups = buildNewTargetGroups(sourceGroups);
          const u = await updateExcursion(sourceExcursionId, {
            ...baseExcPayload(sourceEx),
            groups: mergedGroups,
          });
          if (u) results.push(u);
        } else {
          const newTargetGroups = buildNewTargetGroups(targetGroups);
          const u = await updateExcursion(targetExcursionId, {
            ...baseExcPayload(targetEx),
            groups: newTargetGroups,
          });
          if (u) results.push(u);
        }
      } else {
        const newSourceGroups = sourceGroups.map((g, i) =>
          i === sourceGroupIndex ? removeCustomer(g, customerId) : toGroupPayload(g),
        );
        const newTargetGroups = buildNewTargetGroups(targetGroups);
        if (sourceExcursionId === targetExcursionId) {
          const mergedGroups = sourceGroups.map((g, i) => {
            if (i === sourceGroupIndex) return removeCustomer(g, customerId);
            if (i === targetGroupIndex) return addCustomer(g, customerId, droppedColor);
            return toGroupPayload(g);
          });
          const u = await updateExcursion(sourceExcursionId, {
            ...baseExcPayload(sourceEx),
            groups: mergedGroups,
          });
          if (u) results.push(u);
        } else {
          const [src, tgt] = await Promise.all([
            updateExcursion(sourceExcursionId, {
              ...baseExcPayload(sourceEx),
              groups: newSourceGroups,
            }),
            updateExcursion(targetExcursionId, {
              ...baseExcPayload(targetEx),
              groups: newTargetGroups,
            }),
          ]);
          if (src) results.push(src);
          if (tgt) results.push(tgt);
        }
      }
      if (results.length > 0) {
        setExcursions((prev) => mergeExcursionsAfterPut(prev, results, periodBounds));
      }
    },
    [
      excursions,
      canEdit,
      updateExcursion,
      setExcursions,
      defaultGroupTemplateId,
      showAlert,
      periodBounds,
    ],
  );

  const handleUserDropOnGroup = React.useCallback(
    async (
      sourceExcursionId: number,
      sourceGroupIndex: number,
      targetExcursionId: number,
      targetGroupIndex: number,
      userId: number,
      userColor: string | null | undefined,
      copy: boolean,
    ) => {
      if (!canEdit) return;
      const sourceEx = excursions.find((e) => e.id === sourceExcursionId);
      const targetEx = excursions.find((e) => e.id === targetExcursionId);
      const sourceGroups = sourceEx?.groups ?? [];
      const targetGroups = targetEx?.groups ?? [];
      if (
        !sourceEx ||
        !targetEx ||
        sourceGroupIndex < 0 ||
        sourceGroupIndex >= sourceGroups.length ||
        targetGroupIndex < 0 ||
        targetGroupIndex > targetGroups.length
      )
        return;
      const droppedColor = groupColorOrFallback(userColor ?? undefined);
      const userRow = (
        u: NonNullable<Parameters<typeof toGroupPayload>[0]["excursion_users"]>[number],
        grp: Parameters<typeof toGroupPayload>[0],
      ): ExcursionUser => ({
        id: u.id,
        color: groupColorOrFallback((u.color ?? grp.color) as string | undefined),
        comments: u.comments,
      });
      const removeUser = (
        g: Parameters<typeof toGroupPayload>[0],
        id: number,
      ): ExcursionGroupRequest => ({
        ...toGroupPayload(g),
        excursion_users: (g.excursion_users ?? [])
          .filter((u) => u.id !== id)
          .map((u) => userRow(u, g)),
      });
      const addUser = (
        g: Parameters<typeof toGroupPayload>[0],
        id: number,
        color: string,
      ): ExcursionGroupRequest => {
        const tail: ExcursionUser = { id, color };
        return {
          ...toGroupPayload(g),
          excursion_users: [...(g.excursion_users ?? []).map((u) => userRow(u, g)), tail],
        };
      };
      const newGroupPayloadWithUser = (): ExcursionGroupRequest => ({
        excursion_customers: [],
        excursion_users: [{ id: userId, color: droppedColor }],
        ...(defaultGroupTemplateId != null
          ? { excursion_group_param_id: defaultGroupTemplateId }
          : {}),
        color: droppedColor,
      });
      const buildNewTargetGroups = (groups: typeof targetGroups): ExcursionGroupRequest[] =>
        targetGroupIndex === groups.length
          ? [...groups.map(toGroupPayload), newGroupPayloadWithUser()]
          : groups.map((g, i) =>
              i === targetGroupIndex ? addUser(g, userId, droppedColor) : toGroupPayload(g),
            );

      const sameGroupMove =
        !copy && sourceExcursionId === targetExcursionId && sourceGroupIndex === targetGroupIndex;
      if (sameGroupMove) {
        return;
      }
      if (targetGroupIndex < targetGroups.length && !sameGroupMove) {
        const tg = targetGroups[targetGroupIndex];
        if ((tg.excursion_users ?? []).some((u) => u.id === userId)) {
          showAlert("Cet encadrant est déjà dans ce groupe.", "warning");
          return;
        }
      }

      const results: Excursion[] = [];

      if (copy) {
        if (sourceExcursionId === targetExcursionId) {
          const mergedGroups = buildNewTargetGroups(sourceGroups);
          const u = await updateExcursion(sourceExcursionId, {
            ...baseExcPayload(sourceEx),
            groups: mergedGroups,
          });
          if (u) results.push(u);
        } else {
          const newTargetGroups = buildNewTargetGroups(targetGroups);
          const u = await updateExcursion(targetExcursionId, {
            ...baseExcPayload(targetEx),
            groups: newTargetGroups,
          });
          if (u) results.push(u);
        }
      } else {
        const newSourceGroups = sourceGroups.map((g, i) =>
          i === sourceGroupIndex ? removeUser(g, userId) : toGroupPayload(g),
        );
        const newTargetGroups = buildNewTargetGroups(targetGroups);
        if (sourceExcursionId === targetExcursionId) {
          const mergedGroups = sourceGroups.map((g, i) => {
            if (i === sourceGroupIndex) return removeUser(g, userId);
            if (i === targetGroupIndex) return addUser(g, userId, droppedColor);
            return toGroupPayload(g);
          });
          const u = await updateExcursion(sourceExcursionId, {
            ...baseExcPayload(sourceEx),
            groups: mergedGroups,
          });
          if (u) results.push(u);
        } else {
          const [src, tgt] = await Promise.all([
            updateExcursion(sourceExcursionId, {
              ...baseExcPayload(sourceEx),
              groups: newSourceGroups,
            }),
            updateExcursion(targetExcursionId, {
              ...baseExcPayload(targetEx),
              groups: newTargetGroups,
            }),
          ]);
          if (src) results.push(src);
          if (tgt) results.push(tgt);
        }
      }
      if (results.length > 0) {
        setExcursions((prev) => mergeExcursionsAfterPut(prev, results, periodBounds));
      }
      await silentReloadBookings();
    },
    [
      excursions,
      canEdit,
      updateExcursion,
      setExcursions,
      defaultGroupTemplateId,
      showAlert,
      periodBounds,
      silentReloadBookings,
    ],
  );

  React.useEffect(
    () => () => {
      if (groupsDrawerPostDropTimerRef.current) clearTimeout(groupsDrawerPostDropTimerRef.current);
    },
    [],
  );

  const makeGroupChipDropHandlers = React.useCallback(
    (targetExcursionId: number, targetGroupIndex: number) =>
      canEdit
        ? {
            onDragOver: (e: React.DragEvent<HTMLElement>) => {
              e.preventDefault();
              e.stopPropagation();
              setPlanningTooltipPersonChipDropEffect(e);
            },
            onDrop: async (e: React.DragEvent<HTMLElement>) => {
              e.preventDefault();
              e.stopPropagation();
              const raw = e.dataTransfer.getData("application/json");
              if (!raw) return;
              const copy = planningTooltipDragWantsCopy(e);
              try {
                const d = JSON.parse(raw) as {
                  type: string;
                  sourceExcursionId?: number;
                  sourceGroupIndex?: number;
                  customer_id?: number;
                  customer_label?: string;
                  customer_color?: string | null;
                  user_id?: number;
                  user_color?: string | null;
                };
                if (
                  d.type === "client" &&
                  typeof d.sourceExcursionId === "number" &&
                  typeof d.sourceGroupIndex === "number" &&
                  typeof d.customer_id === "number"
                ) {
                  await withChipDropPending(
                    {
                      excursionId: targetExcursionId,
                      surface: "group",
                      groupIndex: targetGroupIndex,
                    },
                    () =>
                      handleClientDropOnGroup(
                        d.sourceExcursionId!,
                        d.sourceGroupIndex!,
                        targetExcursionId,
                        targetGroupIndex,
                        d.customer_id!,
                        d.customer_color,
                        copy,
                      ),
                  );
                  return;
                }
                if (
                  d.type === "user" &&
                  typeof d.sourceExcursionId === "number" &&
                  typeof d.sourceGroupIndex === "number" &&
                  typeof d.user_id === "number"
                ) {
                  await withChipDropPending(
                    {
                      excursionId: targetExcursionId,
                      surface: "group",
                      groupIndex: targetGroupIndex,
                    },
                    () =>
                      handleUserDropOnGroup(
                        d.sourceExcursionId!,
                        d.sourceGroupIndex!,
                        targetExcursionId,
                        targetGroupIndex,
                        d.user_id!,
                        d.user_color,
                        copy,
                      ),
                  );
                }
              } catch (_) {
                /* ignore */
              }
            },
          }
        : {},
    [canEdit, handleClientDropOnGroup, handleUserDropOnGroup, withChipDropPending],
  );

  const handleMoveExcursion = React.useCallback(
    async (excursionId: number, newDate: string, newStartTime: string, newEndTime: string) => {
      if (!canEdit) return;
      const ex = excursions.find((e) => e.id === excursionId);
      if (!ex) return;
      const groups = (ex.groups ?? []).map(toGroupPayload);
      const updated = await updateExcursion(excursionId, {
        ...baseExcPayload(ex),
        date: newDate,
        start_time: newStartTime,
        end_time: newEndTime,
        groups,
      });
      if (updated) {
        setExcursions((prev) => mergeExcursionsAfterPut(prev, [updated], periodBounds));
      } else {
        await loadExcursions();
      }
    },
    [excursions, canEdit, updateExcursion, setExcursions, loadExcursions, periodBounds],
  );

  const handleExcursionResize = React.useCallback(
    (excursionId: number, newStartMinutes: number, newEndMinutes: number) => {
      const ex = excursions.find((e) => e.id === excursionId);
      if (!ex) return;
      const newDate = ex.date.slice(0, 10);
      handleMoveExcursion(
        excursionId,
        newDate,
        minutesToTimeStr(newStartMinutes),
        minutesToTimeStr(newEndMinutes),
      );
    },
    [excursions, handleMoveExcursion],
  );

  const handleExcursionDrop = React.useCallback(
    (day: Date, startMinutes: number, endMinutes: number, excursionId: number) => {
      setDragProjection(null);
      const newDate = toDateKey(day);
      const startRounded = roundToHalfHour(startMinutes);
      const endRounded = roundToHalfHour(endMinutes);
      const endSafe = Math.max(endRounded, startRounded + HALF_HOUR);
      handleMoveExcursion(
        excursionId,
        newDate,
        minutesToTimeStr(startRounded),
        minutesToTimeStr(endSafe),
      );
    },
    [handleMoveExcursion],
  );

  const onExcursionDragOver = React.useCallback(
    (day: Date, startMinutes: number, _durationMinutes: number) => {
      setDragProjection({ day, startMinutes: roundToHalfHour(startMinutes) });
    },
    [],
  );

  const handleExcursionDropOnDay = React.useCallback(
    (day: Date, excursionId: number) => {
      setDragProjection(null);
      const ex = excursions.find((e) => e.id === excursionId);
      if (!ex) return;
      const newDate = toDateKey(day);
      const { startMinutes, endMinutes } = excursionToMinutes(ex);
      const startRounded = roundToHalfHour(startMinutes);
      const durationRounded = roundToHalfHour(Math.max(HALF_HOUR, endMinutes - startMinutes));
      const endRounded = startRounded + durationRounded;
      handleMoveExcursion(
        excursionId,
        newDate,
        minutesToTimeStr(startRounded),
        minutesToTimeStr(endRounded),
      );
    },
    [excursions, handleMoveExcursion],
  );

  const makeExcursionCardDragHandlers = React.useCallback(
    (ex: Excursion) => {
      if (!canEdit) return {};
      const { startMinutes, endMinutes } = excursionToMinutes(ex);
      const durationMinutes = roundToHalfHour(Math.max(HALF_HOUR, endMinutes - startMinutes));
      return {
        draggable: true,
        onDragStart: (e: React.DragEvent) => {
          const cardRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          const dragOffsetY = e.clientY - cardRect.top;
          e.dataTransfer.setData(
            "application/json",
            JSON.stringify({
              type: "excursion",
              excursionId: ex.id,
              durationMinutes,
              dragOffsetY,
            }),
          );
          e.dataTransfer.effectAllowed = "move";
          setDraggingExcursion({ excursionId: ex.id, durationMinutes, dragOffsetY });
        },
        onDragEnd: () => {
          setDraggingExcursion(null);
          setDragProjection(null);
        },
      };
    },
    [canEdit],
  );

  const makeExcursionCardDropHandlers = React.useCallback(
    (targetExcursionId: number) =>
      canEdit
        ? {
            onDragOver: (e: React.DragEvent<HTMLElement>) => {
              e.preventDefault();
              e.stopPropagation();
              setPlanningTooltipPersonChipDropEffect(e);
            },
            onDrop: async (e: React.DragEvent<HTMLElement>) => {
              e.preventDefault();
              e.stopPropagation();
              const raw = e.dataTransfer.getData("application/json");
              if (!raw) return;
              const copy = planningTooltipDragWantsCopy(e);
              try {
                const d = JSON.parse(raw) as {
                  type: string;
                  sourceExcursionId?: number;
                  sourceGroupIndex?: number;
                  customer_id?: number;
                  customer_label?: string;
                  customer_color?: string | null;
                  user_id?: number;
                  user_color?: string | null;
                  excursionId?: number;
                  durationMinutes?: number;
                };
                if (
                  d.type === "excursion" &&
                  typeof d.excursionId === "number" &&
                  typeof d.durationMinutes === "number"
                ) {
                  const ex = excursions.find((x) => x.id === d.excursionId);
                  if (ex && d.excursionId !== targetExcursionId) {
                    const targetEx = excursions.find((x) => x.id === targetExcursionId);
                    if (targetEx) {
                      setDragProjection(null);
                      const newDate = targetEx.date.slice(0, 10);
                      const { startMinutes } = excursionToMinutes(targetEx);
                      const startRounded = roundToHalfHour(startMinutes);
                      const duration = roundToHalfHour(Math.max(HALF_HOUR, d.durationMinutes));
                      const newEnd = startRounded + duration;
                      await withChipDropPending(
                        { excursionId: targetExcursionId, surface: "card" },
                        () =>
                          handleMoveExcursion(
                            d.excursionId!,
                            newDate,
                            minutesToTimeStr(startRounded),
                            minutesToTimeStr(newEnd),
                          ),
                      );
                    }
                  }
                  return;
                }
                if (
                  d.type === "client" &&
                  typeof d.sourceExcursionId === "number" &&
                  typeof d.sourceGroupIndex === "number" &&
                  typeof d.customer_id === "number" &&
                  d.sourceExcursionId !== targetExcursionId
                ) {
                  const targetEx = excursions.find((x) => x.id === targetExcursionId);
                  if (targetEx) {
                    const targetGroupIndex = targetEx.groups?.length ?? 0;
                    await withChipDropPending(
                      { excursionId: targetExcursionId, surface: "card" },
                      () =>
                        handleClientDropOnGroup(
                          d.sourceExcursionId!,
                          d.sourceGroupIndex!,
                          targetExcursionId,
                          targetGroupIndex,
                          d.customer_id!,
                          d.customer_color,
                          copy,
                        ),
                    );
                  }
                }
                if (
                  d.type === "user" &&
                  typeof d.sourceExcursionId === "number" &&
                  typeof d.sourceGroupIndex === "number" &&
                  typeof d.user_id === "number" &&
                  d.sourceExcursionId !== targetExcursionId
                ) {
                  const targetEx = excursions.find((x) => x.id === targetExcursionId);
                  if (targetEx) {
                    const targetGroupIndex = targetEx.groups?.length ?? 0;
                    await withChipDropPending(
                      { excursionId: targetExcursionId, surface: "card" },
                      () =>
                        handleUserDropOnGroup(
                          d.sourceExcursionId!,
                          d.sourceGroupIndex!,
                          targetExcursionId,
                          targetGroupIndex,
                          d.user_id!,
                          d.user_color,
                          copy,
                        ),
                    );
                  }
                }
              } catch (_) {
                /* ignore */
              }
            },
          }
        : {},
    [
      canEdit,
      excursions,
      handleMoveExcursion,
      handleClientDropOnGroup,
      handleUserDropOnGroup,
      withChipDropPending,
    ],
  );

  return {
    loading: data.loading,
    viewMode: calendar.viewMode,
    setViewMode: calendar.setViewMode,
    currentDate: calendar.currentDate,
    setCurrentDate: calendar.setCurrentDate,
    days: calendar.days,
    monthGridOffset: calendar.monthGridOffset,
    formatPeriodTitle: calendar.formatPeriodTitle,
    navigatePeriod: calendar.navigatePeriod,
    getExcursionsForDay,
    getExcursionsForSlot,
    canEdit,
    canSendSMS,
    canOpenExcursionDrawer,
    modalOpen,
    initialDate,
    initialStartTime,
    initialEndTime,
    editingExcursion,
    handleDayClick,
    openModalForSlot,
    handleExcursionClick,
    handleCloseModal,
    handleSave,
    handleDelete,
    persistExcursionAndKeepModalOpen,
    hoursStart,
    hoursEnd,
    showTimeSlots,
    silentReload,
    getGroupLabels,
    getCustomerInProgressTrainingsTooltip,
    rawCustomers,
    excursions,
    groupsDrawerExcursionId,
    openPlanningGroupsDrawer,
    closePlanningGroupsDrawer,
    handleDragEnterCard,
    handleDragLeaveCard,
    planningChipDragTooltipTargetId,
    dismissPlanningForcedPopoverTarget,
    chipDropPending,
    makeGroupChipDropHandlers,
    makeExcursionCardDragHandlers,
    makeExcursionCardDropHandlers,
    handleExcursionDrop,
    handleExcursionResize,
    onExcursionDragOver,
    draggingExcursion,
    dragProjection,
    handleExcursionDropOnDay,
    clientChipDragInProgress,
    clientChipDragSourceExcursionId,
    onClientChipDragStart,
    onClientChipDragEnd,
    excursionTemplatesList,
    bookingStatusMap,
  };
}
